(() => {
  if (window.KSCartUpsellsFixed) return;
  window.KSCartUpsellsFixed = true;

  const SELECTORS = {
    form: '[data-ks-upsell-form]',
    addButton: '[data-ks-upsell-add]',
    variantSelect: '[data-ks-upsell-variant]',
    defaultVariant: '[data-ks-upsell-default-variant]',
    quantity: '[data-ks-upsell-quantity]',
    buttonTitle: '[data-ks-upsell-button-title]',
    buttonSubtext: '[data-ks-upsell-button-subtext]',
    currentPrice: '.ks-upsell-current-price',
    comparePrice: '.ks-upsell-compare-price',
    savingsBadge: '.ks-upsell-save-badge',
    image: '.ks-upsell-image'
  };

  const toPositiveInteger = (value, fallback = 1) => {
    const number = Number.parseInt(value, 10);

    if (!Number.isFinite(number) || number < 1) {
      return fallback;
    }

    return number;
  };

  /**
   * Clears any custom validity added by the theme or another application.
   */
  const clearFormValidity = (form) => {
    if (!form) return;

    form
      .querySelectorAll('input, select, button')
      .forEach((element) => {
        if (typeof element.setCustomValidity === 'function') {
          element.setCustomValidity('');
        }

        element.removeAttribute('aria-invalid');
      });
  };

  /**
   * Returns the active variant field.
   */
  const getVariantControl = (form) => {
    return (
      form.querySelector(SELECTORS.variantSelect) ||
      form.querySelector(SELECTORS.defaultVariant) ||
      form.querySelector('[name="id"]')
    );
  };

  /**
   * For a dropdown, quantity information belongs to the selected option.
   * For a default variant, it belongs to the hidden variant input.
   */
  const getVariantDataSource = (variantControl) => {
    if (!variantControl) return null;

    if (
      variantControl instanceof HTMLSelectElement &&
      variantControl.selectedOptions.length
    ) {
      return variantControl.selectedOptions[0];
    }

    return variantControl;
  };

  /**
   * Creates a quantity valid for Shopify's variant quantity rules.
   *
   * Example:
   * minimum 3, increment 2:
   * valid quantities become 3, 5, 7, 9...
   */
  const calculateValidQuantity = (
    requestedQuantity,
    dataSource
  ) => {
    const minimum = toPositiveInteger(
      dataSource?.dataset.quantityMin,
      1
    );

    const increment = toPositiveInteger(
      dataSource?.dataset.quantityIncrement,
      1
    );

    const maximumValue = Number.parseInt(
      dataSource?.dataset.quantityMax,
      10
    );

    const maximum =
      Number.isFinite(maximumValue) && maximumValue > 0
        ? maximumValue
        : null;

    let quantity = Math.max(
      toPositiveInteger(requestedQuantity, 1),
      minimum
    );

    const differenceFromMinimum = quantity - minimum;
    const remainder = differenceFromMinimum % increment;

    if (remainder !== 0) {
      quantity += increment - remainder;
    }

    if (maximum !== null && quantity > maximum) {
      quantity = maximum;
    }

    return quantity;
  };

  /**
   * Updates image, prices, savings and CTA copy after variant selection.
   */
  const updateVariantDisplay = (select) => {
    const form = select.closest(SELECTORS.form);
    const card = select.closest('.ks-upsell-card');
    const option = select.selectedOptions?.[0];

    if (!form || !card || !option) return;

    clearFormValidity(form);

    const currentPrice = card.querySelector(
      SELECTORS.currentPrice
    );

    const comparePrice = card.querySelector(
      SELECTORS.comparePrice
    );

    const savingsBadge = card.querySelector(
      SELECTORS.savingsBadge
    );

    const productImage = card.querySelector(
      SELECTORS.image
    );

    const buttonSubtext = card.querySelector(
      SELECTORS.buttonSubtext
    );

    const hasSaving =
      option.dataset.hasSaving === 'true';

    if (currentPrice) {
      currentPrice.textContent =
        option.dataset.price || '';
    }

    if (comparePrice) {
      comparePrice.textContent =
        option.dataset.comparePrice || '';

      comparePrice.hidden = !hasSaving;
    }

    if (savingsBadge) {
      const saveLabel =
        savingsBadge.dataset.saveLabel || 'SAVE';

      savingsBadge.textContent = hasSaving
        ? `${saveLabel} ${option.dataset.savings || ''}`
        : '';

      savingsBadge.hidden = !hasSaving;
    }

    if (buttonSubtext) {
      const hasFixedSubtext =
        buttonSubtext.dataset.fixedSubtext === 'true';

      if (!hasFixedSubtext) {
        buttonSubtext.textContent =
          option.dataset.subtext || 'Add to my order';
      }
    }

    if (
      productImage &&
      option.dataset.image &&
      productImage.src !== option.dataset.image
    ) {
      productImage.style.opacity = '0.45';
      productImage.src = option.dataset.image;
      productImage.srcset = '';

      const restoreImageOpacity = () => {
        productImage.style.opacity = '1';
      };

      if (productImage.complete) {
        restoreImageOpacity();
      } else {
        productImage.addEventListener(
          'load',
          restoreImageOpacity,
          { once: true }
        );

        productImage.addEventListener(
          'error',
          restoreImageOpacity,
          { once: true }
        );
      }
    }
  };

  const getSectionsToRender = (cartDrawer) => {
    if (
      cartDrawer &&
      typeof cartDrawer.getSectionsToRender === 'function'
    ) {
      const sectionIds = cartDrawer
        .getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);

      if (sectionIds.length) {
        return sectionIds;
      }
    }

    return [
      'cart-drawer',
      'cart-icon-bubble'
    ];
  };

  const showButtonError = (
    buttonTitle,
    originalText,
    message
  ) => {
    if (!buttonTitle) return;

    buttonTitle.textContent =
      message || 'Please try again';

    window.setTimeout(() => {
      if (document.contains(buttonTitle)) {
        buttonTitle.textContent = originalText;
      }
    }, 2500);
  };

  const addUpsellProduct = async (button) => {
    const form = button.closest(SELECTORS.form);

    if (!form || button.disabled) return;

    clearFormValidity(form);

    const variantControl = getVariantControl(form);
    const variantDataSource =
      getVariantDataSource(variantControl);

    const variantId = Number.parseInt(
      variantControl?.value,
      10
    );

    const quantityInput = form.querySelector(
      SELECTORS.quantity
    );

    const requestedQuantity =
      quantityInput?.value || 1;

    const quantity = calculateValidQuantity(
      requestedQuantity,
      variantDataSource
    );

    const buttonTitle = form.querySelector(
      SELECTORS.buttonTitle
    );

    const originalButtonText =
      buttonTitle?.textContent.trim() ||
      'Add to cart';

    if (!Number.isFinite(variantId) || variantId < 1) {
      showButtonError(
        buttonTitle,
        originalButtonText,
        'Select a valid option'
      );

      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    if (buttonTitle) {
      buttonTitle.textContent = 'Adding...';
    }

    try {
      const cartDrawer =
        document.querySelector('cart-drawer');

      const sectionIds =
        getSectionsToRender(cartDrawer);

      const shopifyRoot =
        window.Shopify?.routes?.root || '/';

      const cartAddUrl =
        `${shopifyRoot}cart/add.js`;

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity
            }
          ],
          sections: sectionIds,
          sections_url: window.location.pathname
        })
      });

      const responseData = await response.json();

      if (
        !response.ok ||
        responseData.status >= 400
      ) {
        throw new Error(
          responseData.description ||
          responseData.message ||
          'This product could not be added.'
        );
      }

      if (
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        responseData.sections
      ) {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(responseData);

        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error(
        'KS cart upsell add error:',
        error
      );

      showButtonError(
        buttonTitle,
        originalButtonText,
        error.message
      );
    } finally {
      if (document.contains(button)) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }

      if (
        buttonTitle &&
        document.contains(buttonTitle) &&
        buttonTitle.textContent.trim() === 'Adding...'
      ) {
        buttonTitle.textContent =
          originalButtonText;
      }
    }
  };

  /**
   * Capture the upsell variant change before generic theme listeners.
   */
  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest?.(
        SELECTORS.variantSelect
      );

      if (!select) return;

      event.stopImmediatePropagation();
      updateVariantDisplay(select);
    },
    true
  );

  /**
   * Capture the upsell button before generic product-form handlers.
   */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(
        SELECTORS.addButton
      );

      if (!button) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      addUpsellProduct(button);
    },
    true
  );

  /**
   * Safety fallback: the upsell must never submit as a normal form.
   */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.(
        SELECTORS.form
      );

      if (!form) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      clearFormValidity(form);
    },
    true
  );
})();